import { computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { trackEvent } from '@utils/bvPixel';
import { useProduct } from '@modules/product';
import { useProductFlags } from '@modules/featureControl';
import useSelectedMemberId from '@modules/latchkey/composables/useSelectedMemberId';
import {
  GET_REVIEWS,
  GET_REVIEWS_LOADING_STATE,
  GET_REVIEWS_PAGE_NUMBER,
  GET_REVIEWS_STAR_FILTERING,
  GET_REVIEWS_SORT_BY,
  GET_REVIEWS_FREQUENTLY_MENTIONED,
  GET_REVIEWS_SELECTED_FREQUENTLY_MENTIONED,
  GET_REVIEWS_TOTAL_RESULTS,
  GET_ALL_PRODUCTS_DATA,
} from '../../types/getters';
import {
  FETCH_FREQUENTLY_MENTIONED, FETCH_REVIEWS, FETCH_REVIEWS_PHOTOS, RESET_FILTERS,
} from '../../types/actions';
import { reviewsStore } from '../../types/storeNames';
import { SET_IS_CONDENSED } from '../../types/setters';

/**
 *
 * @param {*} store
 * @returns
 */
function getProducts(store) {
  return computed(() => store.getters[`productStore/${GET_ALL_PRODUCTS_DATA}`]);
}

/**
*/
export function useReviews() {
  const store = useStore();
  const { productId: id } = useProduct();
  const { isConsolidatedThirdVariantFlag } = useProductFlags();
  const selectedMemberId = useSelectedMemberId(id);

  const selectedProductId = computed(() => (isConsolidatedThirdVariantFlag ? selectedMemberId.value : id));

  const products = getProducts(store);

  const fetchReviews = () => store.dispatch(`${reviewsStore}/${FETCH_REVIEWS}`, { productId: selectedProductId.value });
  fetchReviews.frequentlyMentioned = () => store.dispatch(`${reviewsStore}/${FETCH_FREQUENTLY_MENTIONED}`, selectedProductId.value);
  fetchReviews.photos = () => store.dispatch(`${reviewsStore}/${FETCH_REVIEWS_PHOTOS}`, selectedProductId.value);

  return {
    /**
     * @type {import('vue').ComputedRef<Review[]>}
     */
    reviews: computed(() => store.getters[`${reviewsStore}/${GET_REVIEWS}`]),
    /**
     * @type {import('vue').ComputedRef<string>}
     */
    loadingState: computed(() => store.getters[`${reviewsStore}/${GET_REVIEWS_LOADING_STATE}`]),
    /**
     * @type {import('vue').ComputedRef<number>}
     */
    page: computed(() => store.getters[`${reviewsStore}/${GET_REVIEWS_PAGE_NUMBER}`]),
    /**
     * @type {import('vue').ComputedRef<number | 'ALL_STAR_RATINGS'>}
     */
    starFiltering: computed(() => store.getters[`${reviewsStore}/${GET_REVIEWS_STAR_FILTERING}`]),
    /**
     * @type {import('vue').ComputedRef<string>}
     */
    sortBy: computed(() => store.getters[`${reviewsStore}/${GET_REVIEWS_SORT_BY}`]),
    /**
     * @type {import('vue').ComputedRef<string>}
     */
    searchQuery: computed(() => store.state.reviewsStore.searchQuery),
    /**
     * @type {import('vue').ComputedRef<Nullable<number>>}
     */
    totalResults: computed(() => store.getters[`${reviewsStore}/${GET_REVIEWS_TOTAL_RESULTS}`]),
    /**
     * @type {import('vue').ComputedRef<Nullable<number>>}
     */
    currentResults: computed(() => store.state.reviewsStore.currentResults),
    /**
     * @type {import('vue').ComputedRef<Nullable<string[]>>}
     */
    frequentlyMentioned: computed(() => store.getters[`${reviewsStore}/${GET_REVIEWS_FREQUENTLY_MENTIONED}`]),
    /**
     * @type {import('vue').ComputedRef<Record<string, ReviewProduct>>}
     */
    relatedProducts: computed(() => products.value[id]?.review?.includes?.products || {}),
    reviewsHeader: computed(() => store.state.reviewsStore.reviewsHeader),
    /**
     * @type {import('vue').ComputedRef<boolean>}
     */
    isFiltered: computed(() => store.state.reviewsStore.isFiltered),
    /**
     * @type {import('vue').WritableComputedRef<Nullable<string>>}
     */
    selectedFrequentlyMentioned: computed({
      get() {
        return store.getters[`${reviewsStore}/${GET_REVIEWS_SELECTED_FREQUENTLY_MENTIONED}`];
      },
      set(newValue) {
        store.dispatch(`${reviewsStore}/handleSelectedFrequentlyMentionedChange`, newValue);
        trackEvent('filter', id, 'frequently_mentioned', newValue);
      },
    }),
    fetchReviews,
    /**
     * @param {string} newValue
     */
    handleSortByChange: (newValue) => {
      store.dispatch(`${reviewsStore}/handleSortByChange`, newValue);
    },
    /**
     * @param {string | 'ALL_STAR_RATINGS'} newValue
     */
    handleFilterByChange: (newValue) => {
      // newValue is either a string of "1" to "5", or "ALL_STAR_RATINGS"
      const parsedValue = parseInt(newValue, 10) || newValue;
      store.dispatch(`${reviewsStore}/handleStarRatingChange`, parsedValue);
      nextTick(() => {
        document.getElementById('reviewFilterBy')?.scrollIntoView({
          behavior: 'smooth',
        });
      });
    },
    /**
     * @param {string[]} commentIds
     */
    getComments(commentIds) {
      const rawComments = products.value[id]?.review?.includes?.comments || {};
      const comments = commentIds?.map((commentId) => {
        const comment = rawComments[commentId];
        if (comment) {
          return {
            ...comment,
            id: Number(comment.id), // convert ID to number as reviews send ID as number and number is what is expected
          };
        }
        return null; // handle missing comments
      }) || [];

      return comments.filter(Boolean); // remove null entries
    },
    /**
     * @param {string} newValue
     */
    handleSearchQueryChange: (newValue) => {
      store.dispatch(`${reviewsStore}/handleSearchQueryChange`, newValue);
    },
    /**
     * @param {string} page
     */
    handlePageChange: (page) => {
      const parsedValue = parseInt(page, 10);
      store.dispatch(`${reviewsStore}/handlePageChange`, parsedValue);
    },
    resetFilters: () => {
      store.dispatch(`${reviewsStore}/${RESET_FILTERS}`);
    },

    /**
     * @type {import('vue').ComputedRef<boolean>}
     */
    isCondensed: computed(() => store.state.reviewsStore.isReviewsSectionCondensed),
    /**
     * @type {import('vue').ComputedRef<boolean>}
     */
    isCondensable: computed(() => !!store.state.reviewsStore.currentResults),
    toggleIsCondensed: () => {
      store.commit(`${reviewsStore}/${SET_IS_CONDENSED}`, !store.state.reviewsStore.isReviewsSectionCondensed);
      trackEvent('read_reviews', id, store.state.reviewsStore.isReviewsSectionCondensed ? 'condensed' : 'expanded');
    },
    expandReviews: () => {
      store.commit(`${reviewsStore}/${SET_IS_CONDENSED}`, false);
    },
    statistics: computed(() => store.state.reviewsStore.statistics),
  };
}

export default useReviews;
